import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { FolderTree, Plus, Edit, Trash2, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { ImageUpload } from './ImageUpload';
import { ImageManager } from '@/lib/imageManager';
import IconSelector from './IconSelector';
import * as Icons from 'lucide-react';

interface Category {
  id: string;
  name: string;
  description: string | null;
  seo_description: string | null;
  image_url: string | null;
  icon_name: string | null;
  coming_soon?: boolean;
  created_at: string;
  subcategories?: Subcategory[];
  product_count?: number;
}

interface Subcategory {
  id: string;
  name: string;
  description: string | null;
  category_id: string;
  product_count?: number;
}

const CategoriesModule = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [productCounts, setProductCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [isAddCategoryOpen, setIsAddCategoryOpen] = useState(false);
  const [isAddSubcategoryOpen, setIsAddSubcategoryOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [editingSubcategory, setEditingSubcategory] = useState<Subcategory | null>(null);
  const [viewingCategory, setViewingCategory] = useState<Category | null>(null);
  const [newCategory, setNewCategory] = useState({ name: '', description: '', seo_description: '', image_url: '', icon_name: '', coming_soon: false });
  const [newSubcategory, setNewSubcategory] = useState({ name: '', description: '', category_id: '' });

  useEffect(() => {
    fetchCategoriesAndSubcategories();
  }, []);

  const fetchCategoriesAndSubcategories = async () => {
    try {
      const [categoriesResult, subcategoriesResult, productCountsResult] = await Promise.all([
        supabase
          .from('categories')
          .select('*')
          .order('name'),
        supabase
          .from('subcategories')
          .select('*')
          .order('name'),
        supabase
          .from('products')
          .select('category_id, subcategory_id')
      ]);

      if (categoriesResult.error) throw categoriesResult.error;
      if (subcategoriesResult.error) throw subcategoriesResult.error;
      if (productCountsResult.error) throw productCountsResult.error;

      // Calculate product counts
      const categoryCounts: Record<string, number> = {};
      const subcategoryCounts: Record<string, number> = {};

      productCountsResult.data?.forEach(product => {
        if (product.category_id) {
          categoryCounts[product.category_id] = (categoryCounts[product.category_id] || 0) + 1;
        }
        if (product.subcategory_id) {
          subcategoryCounts[product.subcategory_id] = (subcategoryCounts[product.subcategory_id] || 0) + 1;
        }
      });

      // Add product counts to categories and subcategories
      const categoriesWithCounts = categoriesResult.data?.map(category => ({
        ...category,
        product_count: categoryCounts[category.id] || 0
      })) || [];

      const subcategoriesWithCounts = subcategoriesResult.data?.map(subcategory => ({
        ...subcategory,
        product_count: subcategoryCounts[subcategory.id] || 0
      })) || [];

      setCategories(categoriesWithCounts);
      setSubcategories(subcategoriesWithCounts);
      setProductCounts({ ...categoryCounts, ...subcategoryCounts });
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSubcategoriesForCategory = (categoryId: string) => {
    return subcategories.filter(sub => sub.category_id === categoryId);
  };

  const handleAddCategory = async () => {
    try {
      const { error } = await supabase
        .from('categories')
        .insert([newCategory]);

      if (error) throw error;

      toast.success('קטגוריה נוספה בהצלחה');
      setNewCategory({ name: '', description: '', seo_description: '', image_url: '', icon_name: '', coming_soon: false });
      setIsAddCategoryOpen(false);
      fetchCategoriesAndSubcategories();
    } catch (error) {
      console.error('Error adding category:', error);
      toast.error('שגיאה בהוספת קטגוריה');
    }
  };

  const handleAddSubcategory = async () => {
    try {
      const { error } = await supabase
        .from('subcategories')
        .insert([newSubcategory]);

      if (error) throw error;

      toast.success('תת-קטגוריה נוספה בהצלחה');
      setNewSubcategory({ name: '', description: '', category_id: '' });
      setIsAddSubcategoryOpen(false);
      fetchCategoriesAndSubcategories();
    } catch (error) {
      console.error('Error adding subcategory:', error);
      toast.error('שגיאה בהוספת תת-קטגוריה');
    }
  };

  const handleDeleteCategory = async (id: string) => {
    if (!confirm('האם אתה בטוח שברצונך למחוק קטגוריה זו? פעולה זו תמחק גם את כל תת-הקטגוריות שלה.')) return;

    try {
      const { error } = await supabase.rpc('delete_category_unrestricted', {
        category_id_param: id
      });

      if (error) throw error;

      toast.success('קטגוריה ותת-קטגוריות נמחקו בהצלחה');
      fetchCategoriesAndSubcategories();
    } catch (error: any) {
      console.error('Error deleting category:', error);
      toast.error(error.message || 'שגיאה במחיקת קטגוריה');
    }
  };

  const handleEditCategory = async (updatedCategory: Category) => {
    try {
      const { error } = await supabase
        .from('categories')
        .update({
          name: updatedCategory.name,
          description: updatedCategory.description,
          seo_description: updatedCategory.seo_description,
          image_url: updatedCategory.image_url,
          icon_name: updatedCategory.icon_name,
          coming_soon: updatedCategory.coming_soon || false
        })
        .eq('id', updatedCategory.id);

      if (error) throw error;

      toast.success('קטגוריה עודכנה בהצלחה');
      setEditingCategory(null);
      fetchCategoriesAndSubcategories();
    } catch (error) {
      console.error('Error updating category:', error);
      toast.error('שגיאה בעדכון קטגוריה');
    }
  };

  const handleEditSubcategory = async (updatedSubcategory: Subcategory) => {
    try {
      const { error } = await supabase
        .from('subcategories')
        .update({
          name: updatedSubcategory.name,
          description: updatedSubcategory.description,
          category_id: updatedSubcategory.category_id
        })
        .eq('id', updatedSubcategory.id);

      if (error) throw error;

      toast.success('תת-קטגוריה עודכנה בהצלחה');
      setEditingSubcategory(null);
      fetchCategoriesAndSubcategories();
    } catch (error) {
      console.error('Error updating subcategory:', error);
      toast.error('שגיאה בעדכון תת-קטגוריה');
    }
  };

  const handleDeleteSubcategory = async (id: string) => {
    if (!confirm('האם אתה בטוח שברצונך למחוק תת-קטגוריה זו?')) return;

    try {
      const { error } = await supabase.rpc('delete_subcategory_unrestricted', {
        subcategory_id_param: id
      });

      if (error) throw error;

      toast.success('תת-קטגוריה נמחקה בהצלחה');
      fetchCategoriesAndSubcategories();
    } catch (error: any) {
      console.error('Error deleting subcategory:', error);
      toast.error(error.message || 'שגיאה במחיקת תת-קטגוריה');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">טוען קטגוריות...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir="rtl">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">סה"כ קטגוריות</CardTitle>
            <FolderTree className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">{categories.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">תת-קטגוריות</CardTitle>
            <FolderTree className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">{subcategories.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">ממוצע תת-קטגוריות</CardTitle>
            <FolderTree className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">
              {categories.length > 0 ? (subcategories.length / categories.length).toFixed(1) : 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Categories Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <CardTitle className="flex items-center gap-2">
              <FolderTree className="h-5 w-5" />
              קטגוריות ותת-קטגוריות
            </CardTitle>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <Dialog open={isAddSubcategoryOpen} onOpenChange={setIsAddSubcategoryOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="gap-2 w-full sm:w-auto">
                    <Plus className="h-4 w-4" />
                    תת-קטגוריה
                  </Button>
                </DialogTrigger>
                <DialogContent className="text-right" dir="rtl">
                  <DialogHeader>
                    <DialogTitle>הוספת תת-קטגוריה חדשה</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="subcategory-name">שם התת-קטגוריה</Label>
                      <Input
                        id="subcategory-name"
                        value={newSubcategory.name}
                        onChange={(e) => setNewSubcategory({ ...newSubcategory, name: e.target.value })}
                        placeholder="הזן שם לתת-קטגוריה"
                      />
                    </div>
                    <div>
                      <Label htmlFor="subcategory-category">קטגוריה אב</Label>
                      <Select value={newSubcategory.category_id} onValueChange={(value) => setNewSubcategory({ ...newSubcategory, category_id: value })}>
                        <SelectTrigger>
                          <SelectValue placeholder="בחר קטגוריה" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map(category => (
                            <SelectItem key={category.id} value={category.id}>
                              {category.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="subcategory-description">תיאור</Label>
                      <Textarea
                        id="subcategory-description"
                        value={newSubcategory.description}
                        onChange={(e) => setNewSubcategory({ ...newSubcategory, description: e.target.value })}
                        placeholder="תיאור התת-קטגוריה"
                      />
                    </div>
                    <Button onClick={handleAddSubcategory} className="w-full">
                      הוסף תת-קטגוריה
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              <Dialog open={isAddCategoryOpen} onOpenChange={setIsAddCategoryOpen}>
                <DialogTrigger asChild>
                  <Button className="gap-2 w-full sm:w-auto">
                    <Plus className="h-4 w-4" />
                    קטגוריה חדשה
                  </Button>
                </DialogTrigger>
                <DialogContent className="text-right" dir="rtl">
                  <DialogHeader>
                    <DialogTitle>הוספת קטגוריה חדשה</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="category-name">שם הקטגוריה</Label>
                      <Input
                        id="category-name"
                        value={newCategory.name}
                        onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                        placeholder="הזן שם לקטגוריה"
                      />
                    </div>
                    <div>
                      <Label htmlFor="category-description">תיאור קצר</Label>
                      <Input
                        id="category-description"
                        value={newCategory.description}
                        onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                        placeholder="תיאור קצר לקטגוריה (מוצג בכרטיס)"
                      />
                    </div>
                    <div>
                      <Label htmlFor="category-seo-description">תיאור SEO (לקידום אתרים)</Label>
                      <Textarea
                        id="category-seo-description"
                        value={newCategory.seo_description}
                        onChange={(e) => setNewCategory({ ...newCategory, seo_description: e.target.value })}
                        placeholder="תיאור ארוך ומפורט לקידום במנועי חיפוש - יוצג בעמוד הקטגוריה"
                        rows={4}
                      />
                    </div>
                    <div>
                      <IconSelector
                        value={newCategory.icon_name}
                        onValueChange={(iconName) => setNewCategory({ ...newCategory, icon_name: iconName })}
                        label="אייקון קטגוריה"
                      />
                    </div>
                    <div>
                      <ImageUpload
                        currentImageUrl={newCategory.image_url}
                        onImageChange={(url) => setNewCategory({ ...newCategory, image_url: url })}
                        bucketName="category-images"
                        label="תמונת קטגוריה"
                        useImageUtils={true}
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="category-coming-soon"
                        checked={newCategory.coming_soon}
                        onCheckedChange={(checked) => setNewCategory({ ...newCategory, coming_soon: checked as boolean })}
                      />
                      <Label htmlFor="category-coming-soon" className="cursor-pointer">
                        הצג כ'בקרוב' (לא ניתן ללחיצה)
                      </Label>
                    </div>

                    <Button onClick={handleAddCategory} className="w-full">
                      הוסף קטגוריה
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Edit Subcategory Dialog */}
              <Dialog open={!!editingSubcategory} onOpenChange={() => setEditingSubcategory(null)}>
                <DialogContent className="text-right" dir="rtl">
                  <DialogHeader>
                    <DialogTitle>עריכת תת-קטגוריה</DialogTitle>
                  </DialogHeader>
                  {editingSubcategory && (
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="edit-subcategory-name">שם התת-קטגוריה</Label>
                        <Input
                          id="edit-subcategory-name"
                          value={editingSubcategory.name}
                          onChange={(e) => setEditingSubcategory({ ...editingSubcategory, name: e.target.value })}
                          placeholder="הזן שם לתת-קטגוריה"
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit-subcategory-category">קטגוריה אב</Label>
                        <Select
                          value={editingSubcategory.category_id}
                          onValueChange={(value) => setEditingSubcategory({ ...editingSubcategory, category_id: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="בחר קטגוריה" />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map(category => (
                              <SelectItem key={category.id} value={category.id}>
                                {category.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="edit-subcategory-description">תיאור</Label>
                        <Textarea
                          id="edit-subcategory-description"
                          value={editingSubcategory.description || ''}
                          onChange={(e) => setEditingSubcategory({ ...editingSubcategory, description: e.target.value })}
                          placeholder="תיאור התת-קטגוריה"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={() => handleEditSubcategory(editingSubcategory)} className="flex-1">
                          שמור שינויים
                        </Button>
                        <Button variant="outline" onClick={() => setEditingSubcategory(null)} className="flex-1">
                          ביטול
                        </Button>
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4" dir="rtl">
            <Accordion type="multiple" className="w-full space-y-4">
              {categories.map((category) => {
                const categorySubcategories = getSubcategoriesForCategory(category.id);
                const renderCategoryIcon = (iconName: string | null) => {
                  if (!iconName) return <div className="w-8 h-8 bg-muted rounded flex items-center justify-center text-muted-foreground">—</div>;
                  const IconComponent = (Icons as any)[iconName];
                  if (!IconComponent) return <div className="w-8 h-8 bg-muted rounded flex items-center justify-center text-muted-foreground">?</div>;
                  return <IconComponent className="h-6 w-6 text-primary" />;
                };

                return (
                  <AccordionItem key={category.id} value={category.id} className="border rounded-lg">
                    <AccordionTrigger className="px-4 py-3 hover:no-underline">
                      <div className="flex flex-col lg:flex-row lg:items-center gap-4 w-full">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="flex-shrink-0">
                            {renderCategoryIcon(category.icon_name)}
                          </div>
                          <img
                            src={ImageManager.getImageUrl(category.image_url || '', 'category')}
                            alt={category.name}
                            className="w-12 h-12 object-cover rounded-lg flex-shrink-0"
                            onError={ImageManager.handleImageError}
                          />
                          <div className="text-right min-w-0 flex-1">
                            <h3 className="font-semibold text-lg truncate">{category.name}</h3>
                            <p className="text-sm text-muted-foreground line-clamp-1">{category.description || 'אין תיאור'}</p>
                          </div>
                        </div>
                        <div className="flex flex-wrap lg:flex-nowrap items-center gap-3 lg:gap-6 text-sm text-muted-foreground">
                          <span className="whitespace-nowrap">{categorySubcategories.length} תת-קטגוריות</span>
                          <span className="whitespace-nowrap"><span className="font-medium text-primary">{category.product_count || 0}</span> מוצרים</span>
                          <span className="whitespace-nowrap hidden md:inline">{new Date(category.created_at).toLocaleDateString('he-IL')}</span>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4">
                      <div className="space-y-4">
                        {/* Category Actions */}
                        <div className="flex gap-2 justify-end border-b pb-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingCategory(category)}
                            className="gap-2"
                          >
                            <Edit className="h-4 w-4" />
                            עריכה
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setViewingCategory(category)}
                            className="gap-2"
                          >
                            <Eye className="h-4 w-4" />
                            צפייה
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteCategory(category.id)}
                            className="gap-2 text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                            מחיקה
                          </Button>
                        </div>

                        {/* Subcategories */}
                        {categorySubcategories.length > 0 ? (
                          <div className="space-y-3">
                            <h4 className="font-medium text-lg">תת-קטגוריות:</h4>
                            <div className="grid gap-3">
                              {categorySubcategories.map((subcategory) => (
                                <div key={subcategory.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                                  <div>
                                    <h5 className="font-medium">{subcategory.name}</h5>
                                    <p className="text-sm text-muted-foreground">{subcategory.description || 'אין תיאור'}</p>
                                    <p className="text-xs text-muted-foreground">
                                      <span className="font-medium text-primary">{subcategory.product_count || 0}</span> מוצרים
                                    </p>
                                  </div>
                                  <div className="flex gap-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setEditingSubcategory(subcategory)}
                                    >
                                      <Edit className="h-3 w-3" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDeleteSubcategory(subcategory.id)}
                                      className="text-destructive hover:text-destructive"
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-4 text-muted-foreground">
                            אין תת-קטגוריות לקטגוריה זו
                          </div>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>
        </CardContent>
      </Card>

      {/* Subcategories List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FolderTree className="h-5 w-5" />
            כל התת-קטגוריות
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {subcategories.map((subcategory) => {
              const parentCategory = categories.find(cat => cat.id === subcategory.category_id);
              return (
                <Card key={subcategory.id} className="p-4">
                  <div className="flex items-center justify-between text-right" dir="rtl">
                    <div>
                      <h4 className="font-medium text-right">{subcategory.name}</h4>
                      <p className="text-sm text-muted-foreground text-right">
                        {parentCategory?.name}
                      </p>
                      <p className="text-xs text-primary font-medium text-right">
                        {subcategory.product_count || 0} מוצרים
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingSubcategory(subcategory)}
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-destructive"
                        onClick={() => handleDeleteSubcategory(subcategory.id)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* View Category Dialog */}
      <Dialog open={!!viewingCategory} onOpenChange={() => setViewingCategory(null)}>
        <DialogContent className="text-right" dir="rtl">
          <DialogHeader>
            <DialogTitle>צפייה בקטגוריה</DialogTitle>
          </DialogHeader>
          {viewingCategory && (
            <div className="space-y-4">
              <div>
                <Label>שם הקטגוריה</Label>
                <div className="p-2 bg-muted rounded">{viewingCategory.name}</div>
              </div>
              <div>
                <Label>תיאור</Label>
                <div className="p-2 bg-muted rounded">{viewingCategory.description || 'אין תיאור'}</div>
              </div>
              <div>
                <Label>תמונה</Label>
                <img
                  src={ImageManager.getImageUrl(viewingCategory.image_url || '', 'category')}
                  alt={viewingCategory.name}
                  className="w-32 h-32 object-cover rounded-lg"
                  onError={ImageManager.handleImageError}
                />
              </div>
              <div>
                <Label>תת-קטגוריות</Label>
                <div className="p-2 bg-muted rounded">
                  {getSubcategoriesForCategory(viewingCategory.id).length} תת-קטגוריות
                </div>
              </div>
              <div>
                <Label>תאריך יצירה</Label>
                <div className="p-2 bg-muted rounded">
                  {new Date(viewingCategory.created_at).toLocaleDateString('he-IL')}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Category Dialog */}
      <Dialog open={!!editingCategory} onOpenChange={() => setEditingCategory(null)}>
        <DialogContent className="text-right" dir="rtl">
          <DialogHeader>
            <DialogTitle>עריכת קטגוריה</DialogTitle>
          </DialogHeader>
          {editingCategory && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-category-name">שם הקטגוריה</Label>
                <Input
                  id="edit-category-name"
                  value={editingCategory.name}
                  onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                  placeholder="הזן שם לקטגוריה"
                />
              </div>
              <div>
                <Label htmlFor="edit-category-description">תיאור קצר</Label>
                <Input
                  id="edit-category-description"
                  value={editingCategory.description || ''}
                  onChange={(e) => setEditingCategory({ ...editingCategory, description: e.target.value })}
                  placeholder="תיאור קצר לקטגוריה (מוצג בכרטיס)"
                />
              </div>
              <div>
                <Label htmlFor="edit-category-seo-description">תיאור SEO (לקידום אתרים)</Label>
                <Textarea
                  id="edit-category-seo-description"
                  value={editingCategory.seo_description || ''}
                  onChange={(e) => setEditingCategory({ ...editingCategory, seo_description: e.target.value })}
                  placeholder="תיאור ארוך ומפורט לקידום במנועי חיפוש - יוצג בעמוד הקטגוריה"
                  rows={4}
                />
              </div>
              <div>
                <IconSelector
                  value={editingCategory.icon_name || ''}
                  onValueChange={(iconName) => setEditingCategory({ ...editingCategory, icon_name: iconName })}
                  label="אייקון קטגוריה"
                />
              </div>
              <div>
                <ImageUpload
                  currentImageUrl={editingCategory.image_url || ''}
                  onImageChange={(url) => setEditingCategory({ ...editingCategory, image_url: url })}
                  bucketName="category-images"
                  label="תמונת קטגוריה"
                  useImageUtils={true}
                />
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="edit-category-coming-soon"
                  checked={editingCategory.coming_soon || false}
                  onCheckedChange={(checked) => setEditingCategory({ ...editingCategory, coming_soon: checked as boolean })}
                />
                <Label htmlFor="edit-category-coming-soon" className="cursor-pointer">
                  הצג כ'בקרוב' (לא ניתן ללחיצה)
                </Label>
              </div>

              <div className="flex gap-2">
                <Button onClick={() => handleEditCategory(editingCategory)} className="flex-1">
                  שמור שינויים
                </Button>
                <Button variant="outline" onClick={() => setEditingCategory(null)} className="flex-1">
                  ביטול
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CategoriesModule;